import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Table, Modal, Badge, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api';
import { useConfirm } from '../../../shared/ConfirmContext';
import PageSection from '../../../components/layout/PageSection';
import AppButton from '../../../components/ui/AppButton';

const EMPTY_UNIT = { name: '', symbol: '', factor_to_mm: '', is_active: true, order: 0 };

export const DIMENSION_UNITS_QUERY_KEY = ['dimension-units'];

export const fetchDimensionUnits = async () => {
  const response = await api.get('settings/api/dimension-units/?page_size=100');
  return Array.isArray(response) ? response : (response?.results || []);
};

/** Management of configurable dimension (length) units: table, add/edit, default unit and converter. */
const DimensionUnitsSection = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState(EMPTY_UNIT);
  const [unitError, setUnitError] = useState(null);

  const [convValue, setConvValue] = useState('');
  const [convFrom, setConvFrom] = useState('');
  const [convTo, setConvTo] = useState('');
  const [convResult, setConvResult] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  const { data: unitsData } = useQuery({
    queryKey: DIMENSION_UNITS_QUERY_KEY,
    queryFn: fetchDimensionUnits,
  });
  const units = unitsData || [];
  const activeUnits = units.filter(u => u.is_active);

  useEffect(() => {
    if (!unitsData) return;
    const active = unitsData.filter(u => u.is_active);
    if (!convFrom && active.length) setConvFrom(String(active[0].id));
    if (!convTo && active.length > 1) setConvTo(String(active[1].id));
  }, [unitsData]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: DIMENSION_UNITS_QUERY_KEY });

  const unitSaveMutation = useMutation({
    mutationFn: (payload) => editingUnit
      ? api.patch(`settings/api/dimension-units/${editingUnit.id}/`, payload)
      : api.post('settings/api/dimension-units/', payload),
    onSuccess: () => { setShowUnitModal(false); invalidate(); },
    onError: (err) => setUnitError(err.message || t('settings.dimensionUnits.errors.saveFailed')),
  });

  const unitDeleteMutation = useMutation({
    mutationFn: (id) => api.delete(`settings/api/dimension-units/${id}/`),
    onSuccess: invalidate,
    onError: (err) => setError(err.message || t('settings.dimensionUnits.errors.deleteFailed')),
  });

  const unitDefaultMutation = useMutation({
    mutationFn: (id) => api.post(`settings/api/dimension-units/${id}/set_default/`),
    onSuccess: invalidate,
    onError: (err) => setError(err.message || t('settings.dimensionUnits.errors.saveFailed')),
  });
  const unitsBusy = unitDeleteMutation.isPending || unitDefaultMutation.isPending;

  const openAddUnit = () => {
    setEditingUnit(null);
    setUnitForm(EMPTY_UNIT);
    setUnitError(null);
    setShowUnitModal(true);
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({ name: unit.name, symbol: unit.symbol, factor_to_mm: unit.factor_to_mm, is_active: unit.is_active, order: unit.order });
    setUnitError(null);
    setShowUnitModal(true);
  };

  const handleUnitSubmit = (e) => {
    e.preventDefault();
    setUnitError(null);
    const payload = { ...unitForm, factor_to_mm: parseFloat(unitForm.factor_to_mm), order: parseInt(unitForm.order, 10) || 0 };
    unitSaveMutation.mutate(payload);
  };

  const handleDeleteUnit = async (unit) => {
    if (!await confirm(t('settings.dimensionUnits.deleteConfirm'))) return;
    unitDeleteMutation.mutate(unit.id);
  };

  const handleConvert = async () => {
    if (!convValue || !convFrom || !convTo) return;
    try {
      setConvLoading(true);
      const res = await api.post('settings/api/dimension-units/convert/', {
        value: parseFloat(convValue),
        from_unit: convFrom,
        to_unit: convTo,
      });
      setConvResult(res);
    } catch (err) {
      setConvResult({ error: err.message });
    } finally {
      setConvLoading(false);
    }
  };

  return (
    <>
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

      {/* ── Dimension units management table ── */}
      <Row className="row-deck row-cards mb-3">
        <Col>
          <PageSection
            title={t('settings.dimensionUnits.title')}
            description={t('settings.dimensionUnits.subtitle')}
            bodyClassName="p-0"
            actions={
              <Button size="sm" variant="primary" onClick={openAddUnit}>
                <i className="ti ti-plus me-1"></i>
                {t('settings.dimensionUnits.add')}
              </Button>
            }
          >
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('settings.dimensionUnits.name')}</th>
                  <th>{t('settings.dimensionUnits.symbol')}</th>
                  <th>{t('settings.dimensionUnits.factorToMm')}</th>
                  <th>{t('settings.dimensionUnits.order')}</th>
                  <th>{t('settings.currency.active')}</th>
                  <th className="text-end">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {units.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-3">{t('settings.dimensionUnits.noUnits')}</td></tr>
                )}
                {units.map(unit => (
                  <tr key={unit.id}>
                    <td>
                      <strong>{unit.name}</strong>
                      {unit.is_default && (
                        <Badge bg="primary" className="ms-2">{t('settings.currency.default')}</Badge>
                      )}
                    </td>
                    <td><code>{unit.symbol}</code></td>
                    <td>{unit.factor_to_mm.toLocaleString()}</td>
                    <td>{unit.order}</td>
                    <td>
                      {unit.is_active
                        ? <span className="badge bg-success-lt">{t('settings.currency.active')}</span>
                        : <span className="badge bg-secondary-lt">{t('settings.currency.inactive')}</span>}
                    </td>
                    <td className="text-end">
                      <div className="btn-group">
                        {!unit.is_default && unit.is_active && (
                          <Button size="sm" variant="outline-primary" onClick={() => unitDefaultMutation.mutate(unit.id)} disabled={unitsBusy}>
                            {t('settings.currency.setAsDefault')}
                          </Button>
                        )}
                        <Button size="sm" variant="outline-info" onClick={() => openEditUnit(unit)} title={t('common.edit')}>
                          <i className="ti ti-edit"></i>
                        </Button>
                        {!unit.is_default && (
                          <Button size="sm" variant="outline-danger" onClick={() => handleDeleteUnit(unit)} disabled={unitsBusy} title={t('common.delete')}>
                            <i className="ti ti-trash"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PageSection>
        </Col>
      </Row>

      {/* ── Dimension converter ── */}
      <Row className="row-deck row-cards mb-3">
        <Col md={6}>
          <PageSection title={t('settings.dimensionUnits.converter')}>
            <Row className="g-2 align-items-end">
              <Col>
                <Form.Label>{t('settings.weightUnits.value')}</Form.Label>
                <Form.Control
                  type="number"
                  value={convValue}
                  onChange={e => { setConvValue(e.target.value); setConvResult(null); }}
                  placeholder="0"
                />
              </Col>
              <Col>
                <Form.Label>{t('settings.weightUnits.from')}</Form.Label>
                <Form.Select value={convFrom} onChange={e => { setConvFrom(e.target.value); setConvResult(null); }}>
                  {activeUnits.map(u => <option key={u.id} value={u.id}>{u.symbol}</option>)}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>{t('settings.weightUnits.to')}</Form.Label>
                <Form.Select value={convTo} onChange={e => { setConvTo(e.target.value); setConvResult(null); }}>
                  {activeUnits.map(u => <option key={u.id} value={u.id}>{u.symbol}</option>)}
                </Form.Select>
              </Col>
              <Col xs="auto">
                <Button variant="secondary" onClick={handleConvert} disabled={convLoading || !convValue}>
                  {convLoading ? <span className="spinner-border spinner-border-sm" /> : '='}
                </Button>
              </Col>
            </Row>
            {convResult && !convResult.error && (
              <div className="alert alert-info mt-3 mb-0">
                <strong>{convResult.formula}</strong>
              </div>
            )}
            {convResult?.error && (
              <div className="alert alert-danger mt-3 mb-0">{convResult.error}</div>
            )}
          </PageSection>
        </Col>
      </Row>

      {/* ── Add / Edit unit modal ── */}
      <Modal show={showUnitModal} onHide={() => setShowUnitModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUnit ? t('settings.dimensionUnits.edit') : t('settings.dimensionUnits.add')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUnitSubmit}>
          <Modal.Body>
            {unitError && <Alert variant="danger">{unitError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.dimensionUnits.name')} *</Form.Label>
              <Form.Control
                type="text"
                value={unitForm.name}
                onChange={e => setUnitForm(p => ({ ...p, name: e.target.value }))}
                placeholder={t('settings.dimensionUnits.namePlaceholder')}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.dimensionUnits.symbol')} *</Form.Label>
              <Form.Control
                type="text"
                value={unitForm.symbol}
                onChange={e => setUnitForm(p => ({ ...p, symbol: e.target.value }))}
                placeholder="cm"
                maxLength={10}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.dimensionUnits.factorToMm')} *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                min="0.000001"
                value={unitForm.factor_to_mm}
                onChange={e => setUnitForm(p => ({ ...p, factor_to_mm: e.target.value }))}
                placeholder="10"
                required
              />
              <Form.Text className="text-muted">{t('settings.dimensionUnits.factorHelp')}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.dimensionUnits.order')}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={unitForm.order}
                onChange={e => setUnitForm(p => ({ ...p, order: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="dimension-unit-active"
              label={t('settings.currency.active')}
              checked={unitForm.is_active}
              onChange={e => setUnitForm(p => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUnitModal(false)}>{t('common.cancel')}</Button>
            <AppButton variant="primary" type="submit" loading={unitSaveMutation.isPending} loadingText={t('common.saving')}>
              {t('common.save')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default DimensionUnitsSection;
